const STORAGE_KEY = 'celebrations_audio_settings';
const DEFAULT_SETTINGS = { music: false, soundEffects: true };

let settings = { ...DEFAULT_SETTINGS };
let audioContext = null;
let masterGain = null;
let musicTimer = null;
let musicStep = 0;
const listeners = new Set();

function readSettings() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return {
      music: typeof stored.music === 'boolean' ? stored.music : DEFAULT_SETTINGS.music,
      soundEffects: typeof stored.soundEffects === 'boolean'
        ? stored.soundEffects
        : DEFAULT_SETTINGS.soundEffects,
    };
  } catch (error) {
    console.warn('Could not read audio preferences:', error);
    return { ...DEFAULT_SETTINGS };
  }
}

settings = readSettings();

function getAudioContext() {
  if (audioContext) return audioContext;
  try {
    const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextConstructor) return null;

    audioContext = new AudioContextConstructor();
    masterGain = audioContext.createGain();
    masterGain.gain.value = 0.7;
    masterGain.connect(audioContext.destination);
    return audioContext;
  } catch (error) {
    console.warn('Could not create game audio:', error);
    return null;
  }
}

function playTone(frequency, duration, waveform = 'sine', volume = 0.18, endFrequency = frequency) {
  const context = getAudioContext();
  if (!context || !masterGain || context.state !== 'running') return;

  const oscillator = context.createOscillator();
  const gain = context.createGain();
  const now = context.currentTime;
  oscillator.type = waveform;
  oscillator.frequency.setValueAtTime(frequency, now);
  oscillator.frequency.exponentialRampToValueAtTime(Math.max(1, endFrequency), now + duration);
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(volume, now + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
  oscillator.connect(gain);
  gain.connect(masterGain);
  oscillator.start(now);
  oscillator.stop(now + duration + 0.02);
}

function scheduleMusicStep() {
  if (!settings.music || !audioContext || audioContext.state !== 'running') return;

  const melody = [261.63, 329.63, 392, 329.63, 293.66, 349.23, 440, 349.23];
  const note = melody[musicStep % melody.length];
  playTone(note, 0.42, 'triangle', 0.045, note * 1.002);
  if (musicStep % 4 === 0) playTone(note / 2, 0.9, 'sine', 0.035);
  musicStep += 1;
  musicTimer = window.setTimeout(scheduleMusicStep, 470);
}

function startMusic() {
  window.clearTimeout(musicTimer);
  musicTimer = null;
  if (settings.music && audioContext?.state === 'running') scheduleMusicStep();
}

function saveSettings() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.warn('Could not save audio preferences:', error);
  }
  listeners.forEach(listener => listener(settings));
}

export function getAudioSettings() {
  return { ...settings };
}

export function subscribeAudioSettings(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setAudioSetting(key, value) {
  settings = { ...settings, [key]: value };
  saveSettings();
  if (key === 'music') {
    if (value) startMusic();
    else {
      window.clearTimeout(musicTimer);
      musicTimer = null;
    }
  }
}

export async function unlockGameAudio() {
  const context = getAudioContext();
  if (!context) return false;
  try {
    if (context.state !== 'running') await context.resume();
    if (settings.music) startMusic();
    return context.state === 'running';
  } catch (error) {
    console.warn('Could not start game audio:', error);
    return false;
  }
}

export function playGameSound(name) {
  if (!settings.soundEffects || !audioContext || audioContext.state !== 'running') return;

  const sounds = {
    tap: () => playTone(520, 0.055, 'triangle', 0.055, 430),
    countdown: () => playTone(740, 0.1, 'sine', 0.1, 620),
    start: () => playTone(392, 0.18, 'triangle', 0.12, 587.33),
    move: () => playTone(330, 0.075, 'triangle', 0.09, 392),
    score: () => playTone(660, 0.09, 'sine', 0.075, 740),
    flap: () => playTone(620, 0.08, 'triangle', 0.11, 790),
    roll: () => playTone(180, 0.22, 'triangle', 0.16, 620),
    card: () => playTone(460, 0.11, 'square', 0.075, 320),
    line: () => {
      playTone(523.25, 0.2, 'triangle', 0.12);
      window.setTimeout(() => playTone(783.99, 0.24, 'triangle', 0.1), 75);
    },
    correct: () => {
      playTone(523.25, 0.18, 'triangle', 0.14);
      window.setTimeout(() => playTone(659.25, 0.2, 'triangle', 0.14), 90);
      window.setTimeout(() => playTone(783.99, 0.28, 'triangle', 0.13), 180);
    },
    wrong: () => playTone(240, 0.22, 'triangle', 0.12, 145),
    crash: () => playTone(190, 0.42, 'sawtooth', 0.18, 48),
    win: () => {
      playTone(523.25, 0.2, 'triangle', 0.15);
      window.setTimeout(() => playTone(659.25, 0.2, 'triangle', 0.15), 100);
      window.setTimeout(() => playTone(783.99, 0.2, 'triangle', 0.15), 200);
      window.setTimeout(() => playTone(1046.5, 0.42, 'triangle', 0.14), 300);
    },
  };
  sounds[name]?.();
}

export function resumeGameAudio() {
  return unlockGameAudio();
}

export function pauseGameAudio() {
  window.clearTimeout(musicTimer);
  musicTimer = null;
  if (audioContext?.state === 'running') {
    audioContext.suspend().catch(error => console.warn('Could not pause game audio:', error));
  }
}
